import { QuizEntity, QuizTypeAllItems } from '../../../../meta-data/app-meta-data.service';

/** Row shape from the existing Quiz-For_Grid query. */
export interface QuizRow extends QuizEntity {
  Lessons?: string;
}

export interface QuizRowVm {
  row: QuizRow;
  key: number;
  name: string;
  typeLabel: string;
  typeIcon: string;
  lessons: string;
  description: string;
  contextLine: string;
  passingLabel: string;
  questionLimitLabel: string;
  timeLimitLabel: string;
  attemptLimitLabel: string;
  settings: { label: string; value: string; icon: string | null }[];
}

export interface QuizListFilter {
  search: string;
  typeId: number | null;
  sortKey: 'name' | 'name-desc';
}

export const QUIZ_TYPES = [
  { key: QuizTypeAllItems.Quiz, label: 'Quiz', icon: 'fa-list-alt' },
  { key: QuizTypeAllItems.CourseFinal, label: 'Course final', icon: 'fa-graduation-cap' },
  { key: QuizTypeAllItems.ModuleFinal, label: 'Module final', icon: 'fa-flag-checkered' }
];

export const QUIZ_SORTS = [
  { key: 'name', label: 'Name A–Z' },
  { key: 'name-desc', label: 'Name Z–A' }
];

export function buildQuizRowVm(row: QuizRow): QuizRowVm {
  const type = QUIZ_TYPES.find(t => t.key === row.QuizTypeId);
  const typeLabel = row.QuizTypeIdDisplay || type?.label || 'Quiz';
  // The grid query can list the same lesson more than once ("19.5 Quiz 1, 19.5 Quiz 1, 22.5 Quiz 1").
  const lessons = [...new Set(stripHtml(row.Lessons).split(',').map(lesson => lesson.trim()).filter(Boolean))].join(', ');
  const setting = (label: string, value: boolean) => ({
    label,
    value: value == null ? 'Not specified' : value ? 'Yes' : 'No',
    icon: value == null ? null : value ? 'fa-check' : 'fa-minus'
  });
  return {
    row,
    key: row.QuizId,
    name: row.QuizName || `Quiz #${row.QuizId}`,
    typeLabel,
    typeIcon: type?.icon || 'fa-list-alt',
    lessons,
    description: stripHtml(row.QuizDescription),
    // Type is carried by the row icon, so the context line is just the lessons.
    contextLine: lessons || typeLabel,
    passingLabel: row.PassingPercent == null ? '—' : `${row.PassingPercent}%`,
    questionLimitLabel: row.MaxQuestionsToShow == null ? 'All' : String(row.MaxQuestionsToShow),
    timeLimitLabel: row.TimeLimitMinutes ? `${row.TimeLimitMinutes} min` : 'No limit',
    attemptLimitLabel: row.MaxAttempts == null ? 'No limit' : String(row.MaxAttempts),
    settings: [
      setting('Random question order', row.UseRandomOrder),
      setting('Show answers on pass', row.ShowAnswersOnPass),
      setting('Show answers on fail', row.ShowAnswersOnFail),
      setting('Weighted answers', row.UseWeightedAnswers)
    ]
  };
}

export function filterAndSortQuizVms(vms: QuizRowVm[], filter: QuizListFilter): QuizRowVm[] {
  const search = filter.search.trim().toLowerCase();
  return vms.filter(vm =>
    (filter.typeId == null || vm.row.QuizTypeId === filter.typeId) &&
    (!search || `${vm.name} ${vm.typeLabel} ${vm.contextLine} ${vm.description}`.toLowerCase().includes(search) || String(vm.key) === search)
  ).sort((a, b) => (filter.sortKey === 'name-desc' ? -1 : 1) * a.name.localeCompare(b.name));
}

export function stripHtml(value: string | null | undefined): string {
  const document = new DOMParser().parseFromString(value || '', 'text/html');
  document.querySelectorAll('script, style').forEach(element => element.remove());
  document.querySelectorAll('br, p, div, li').forEach(element => element.appendChild(document.createTextNode(' ')));
  return (document.body.textContent || '').replace(/\s+/g, ' ').trim();
}
